import { TextField } from "@mui/material";
import React, { FC, useCallback, useEffect, useState } from "react";
import { FormBasicsDto } from "@green0meter/api-client";
import FeatherIcon from "feather-icons-react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";

import FormSelect from "./components/FormSelect";
import Table from "./components/Table";

import { useGetQuestionConfigs } from "@api";
import { Stack, Typography, Card, Box, Button } from "@components/base";
import { TableSkeleton } from "@components/common";
import { useUserStore } from "@store/user";


interface QuestionsTable {
  onSelect: (questionIds: string[]) => void;
}

const QuestionsTable: FC<QuestionsTable> = ({ onSelect }) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const [page, setPage] = useState(0);
  const [form, setForm] = useState<FormBasicsDto>();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedQuestions, setSelectedQuestions] = useState<string[]>([]);
  const methods = useFormContext();
  const { questionsConfig, questionsConfigEnabled, isLoadingQuestionsConfig } =
    useGetQuestionConfigs(user, {
      formId: form?.id,
      pageNumber: page + 1,
      pageSize: 50,
      filter: {
        query: searchQuery,
      },
    });
  const values = useWatch({
    control: methods.control,
    name: "questions",
  });

  const handleAddQuestions = (): void => {
    onSelect(selectedQuestions);
  };

  const debounceSearchQuery = useCallback(debounce(setSearchQuery, 1000), []);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const searchValue = event.target.value.toLowerCase();

    debounceSearchQuery(searchValue);
  };

  useEffect(() => {
    if (values) {
      const selectedQuestions = Object.keys(values).filter(
        (key) => values[key] === true,
      );

      setSelectedQuestions(selectedQuestions);
    }
  }, [values]);

  return (
    questionsConfigEnabled && (
      <>
        <Card>
          <Stack
            p={3}
            justifyContent="space-between"
            alignItems="center"
            direction="row"
            spacing={2}
          >
            <Typography variant="subtitle1">
              {t("questionsTable.selectQuestions")}
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                size="small"
                variant="filled"
                slotProps={{
                  input: { startAdornment: <FeatherIcon icon="search" /> },
                }}
                placeholder={t("questionsTable.search")}
                onChange={handleSearch}
              />
              <FormSelect form={form} setForm={setForm} />
            </Stack>
          </Stack>
          {isLoadingQuestionsConfig && <TableSkeleton rows={5} columns={4} />}
          {!isLoadingQuestionsConfig && questionsConfig?.items && (
            <Table
              questions={questionsConfig?.items}
              page={page}
              setPage={setPage}
              control={methods.control}
            />
          )}
        </Card>
        {!isLoadingQuestionsConfig && (
          <Box
            position="sticky"
            bottom={0}
            left={0}
            right={0}
            p={3}
            sx={({ palette: { common, divider } }) => ({
              background: common.white,
              borderTop: `1px solid ${divider}`,
            })}
          >
            <Stack
              justifyContent="space-between"
              alignItems="center"
              direction="row"
              spacing={2}
            >
              <Typography variant="subtitle1">
                {t("questionsTable.selectedCount", {
                  count: selectedQuestions.length,
                })}
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={handleAddQuestions}
                disabled={selectedQuestions.length === 0}
                startIcon={<FeatherIcon icon="check" />}
              >
                {t("questionsTable.addQuestions")}
              </Button>
            </Stack>
          </Box>
        )}
      </>
    )
  );
};

export default QuestionsTable;
